import { Shimmer } from '@components'
import { IAccountBalance } from '@query'
import { formatCurrency } from '@utils'
import { tryFormatIBAN } from '@root/utils/iban'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import styled from 'styled-components'

export const COLLAPSED_ACCOUNT_COUNT = 2

export const NavyShimmer = styled(Shimmer).attrs({
  backgroundColor: 'rgba(255, 255, 255, 0.12)'
})``

export const OptionIcon = styled.div<{ $src: string | null }>`
  width: 1.625rem;
  height: 1.625rem;
  min-width: 1.625rem;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background-color: ${({ theme }) => theme.colors.neutralWhite};
  background-image: ${({ $src }) => ($src ? `url(${$src})` : 'none')};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-origin: content-box;
`

interface Props {
  isLoading: boolean
  visibleAccounts: IAccountBalance[]
  hiddenAccounts: IAccountBalance[]
  expanded: boolean
  selected: boolean
  onToggle: () => void
  accountKey: (account: IAccountBalance) => string
}

export const CashAccounts: React.FC<Props> = ({
  isLoading,
  visibleAccounts,
  hiddenAccounts,
  expanded,
  selected,
  onToggle,
  accountKey
}) => {
  const [t] = useTranslation()

  return (
    <>
      <AccountList $scrollable={expanded && !selected}>
        {isLoading
          ? Array.from({ length: COLLAPSED_ACCOUNT_COUNT }, (_, index) => (
              <AccountRow key={`account-skeleton-${index}`} aria-hidden="true">
                <NavyShimmer width="1.625rem" height="1.625rem" borderRadius="0.375rem" />
                <AccountNames>
                  <NavyShimmer width="7.5rem" height="0.75rem" borderRadius="0.25rem" />
                  <NavyShimmer width="4.75rem" height="0.5rem" borderRadius="0.25rem" />
                </AccountNames>
                <NavyShimmer width="3.25rem" height="0.875rem" borderRadius="0.25rem" />
              </AccountRow>
            ))
          : visibleAccounts.map(account => (
              <AccountRow key={accountKey(account)}>
                <OptionIcon $src={account.icon} />
                <AccountNames>
                  <AccountName>{account.name}</AccountName>
                  <AccountIban>{tryFormatIBAN(account.identifier_short)}</AccountIban>
                </AccountNames>
                <AccountAmount>{formatCurrency(account.balance.amount)}</AccountAmount>
              </AccountRow>
            ))}
      </AccountList>

      {(hiddenAccounts.length > 0 || (expanded && !selected)) && (
        <AccountsToggle type="button" onClick={onToggle}>
          <ToggleLabel>
            {expanded
              ? t('dashboard.cash.showFewer')
              : t('dashboard.cash.showMore', { count: hiddenAccounts.length })}
          </ToggleLabel>
          {!expanded && (
            <ToggleAmount>
              {`+ ${formatCurrency(
                hiddenAccounts.reduce((sum, account) => sum + account.balance.amount, 0)
              )}`}
            </ToggleAmount>
          )}
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
        </AccountsToggle>
      )}
    </>
  )
}

const AccountList = styled.div<{ $scrollable: boolean }>`
  flex: 0 0 auto;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  max-height: ${({ $scrollable }) => ($scrollable ? '9.25rem' : 'none')};
  overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'visible')};
`

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
`

const AccountNames = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  flex: 1;
  min-width: 0;
`

const AccountName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const AccountIban = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  color: ${({ theme }) => theme.dashboard.onNavyMuted};
  font-variant-numeric: tabular-nums;
`

const AccountAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const AccountsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem 0 0;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }

  svg {
    font-size: ${({ theme }) => theme.fontSize.px11}rem;
    color: ${({ theme }) => theme.dashboard.onNavyLabel};
  }
`

const ToggleLabel = styled.span`
  flex: 1;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
`

const ToggleAmount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.dashboard.onNavyMuted};
  font-variant-numeric: tabular-nums;
`
